import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const InternalChangeOrders = () => {
  const location = useLocation();
  const [internalChangeOrders, setInternalChangeOrders] = useState([]);
  const [filteredChangeOrders, setFilteredChangeOrders] = useState([]);
  const [projects, setProjects] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Get project context from URL parameters
  const urlParams = new URLSearchParams(location.search);
  const projectVuid = urlParams.get('project');
  const isCreateMode = urlParams.get('create') === 'true';
  
  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingChangeOrder, setEditingChangeOrder] = useState(null);
  const [formManuallyClosed, setFormManuallyClosed] = useState(false);
  const [formData, setFormData] = useState({
    project_vuid: '',
    original_budget_vuid: '',
    accounting_period_vuid: '',
    description: '',
    change_order_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  
  // Change order lines states
  const [showLinesForm, setShowLinesForm] = useState(false);
  const [selectedChangeOrder, setSelectedChangeOrder] = useState(null);
  const [changeOrderLines, setChangeOrderLines] = useState([]);
  const [newLine, setNewLine] = useState({
    cost_code_vuid: '',
    cost_type_vuid: '',
    change_amount: '',
    notes: ''
  });
  
  // Budget lines state for filtering cost codes/types
  const [budgetLines, setBudgetLines] = useState([]);

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Filter change orders by project and auto-open create form if in create mode
  useEffect(() => {
    if (projectVuid) {
      const filtered = internalChangeOrders.filter(ico => ico.project_vuid === projectVuid);
      setFilteredChangeOrders(filtered);
      
      // Auto-open create form if in create mode (only if form is not already shown and wasn't manually closed)
      if (isCreateMode && !showCreateForm && !formManuallyClosed) {
        handleShowCreateForm();
      }
    } else {
      setFilteredChangeOrders(internalChangeOrders);
    }
  }, [internalChangeOrders, projectVuid, isCreateMode, showCreateForm, formManuallyClosed]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, budgetsRes, costCodesRes, costTypesRes, changeOrdersRes, accountingPeriodsRes] = await Promise.all([
        axios.get(`${baseURL}/api/projects`),
        axios.get(`${baseURL}/api/project-budgets`),
        axios.get(`${baseURL}/api/cost-codes`),
        axios.get(`${baseURL}/api/cost-types`),
        axios.get(`${baseURL}/api/internal-change-orders`),
        axios.get(`${baseURL}/api/accounting-periods`)
      ]);

      setProjects(projectsRes.data);
      setBudgets(budgetsRes.data);
      setCostCodes(costCodesRes.data);
      setCostTypes(costTypesRes.data);
      setInternalChangeOrders(changeOrdersRes.data);
      setAccountingPeriods(accountingPeriodsRes.data);
    } catch (err) {
      setError('Error fetching data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleShowCreateForm = () => {
    setFormManuallyClosed(false);
    setShowCreateForm(true);
    setEditingChangeOrder(null);
    
    // Set default accounting period if only one is open
    let defaultAccountingPeriod = '';
    if (accountingPeriods.length === 1 && accountingPeriods[0].status === 'open') {
      defaultAccountingPeriod = accountingPeriods[0].vuid;
    }
    
    setFormData({
      project_vuid: projectVuid || '', // Pre-populate with project context if available
      original_budget_vuid: '',
      accounting_period_vuid: defaultAccountingPeriod,
      description: '',
      change_order_date: new Date().toISOString().split('T')[0],
      status: 'draft', // Default to draft instead of pending
      notes: ''
    });
  };

  const handleCancelEdit = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setFormManuallyClosed(true);
    setShowCreateForm(false);
    setEditingChangeOrder(null);
    
    // Set default accounting period if only one is open
    let defaultAccountingPeriod = '';
    if (accountingPeriods.length === 1 && accountingPeriods[0].status === 'open') {
      defaultAccountingPeriod = accountingPeriods[0].vuid;
    }
    
    setFormData({
      project_vuid: '',
      original_budget_vuid: '',
      accounting_period_vuid: defaultAccountingPeriod,
      description: '',
      change_order_date: new Date().toISOString().split('T')[0],
      status: 'draft',
      notes: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If original_budget_vuid changes, fetch budget lines
    if (name === 'original_budget_vuid' && value) {
      fetchBudgetLines(value);
    } else if (name === 'original_budget_vuid' && !value) {
      setBudgetLines([]);
    }
  };
  
  // Fetch budget lines for filtering cost codes/types
  const fetchBudgetLines = async (budgetVuid) => {
    if (!budgetVuid) {
      setBudgetLines([]);
      return;
    }
    
    try {
      const response = await axios.get(`${baseURL}/api/project-budgets/${budgetVuid}/lines`);
      setBudgetLines(response.data || []);
    } catch (err) {
      console.error('Error fetching budget lines:', err);
      setBudgetLines([]);
    }
  };
  
  // Get filtered cost codes from budget lines
  const getFilteredCostCodes = () => {
    if (budgetLines.length === 0) {
      return costCodes.filter(c => c.status === 'active');
    }
    
    const budgetCostCodeVuids = new Set(budgetLines.map(line => line.cost_code_vuid));
    return costCodes.filter(code => 
      budgetCostCodeVuids.has(code.vuid) && code.status === 'active'
    );
  };
  
  // Get filtered cost types based on selected cost code
  const getFilteredCostTypes = (selectedCostCodeVuid) => {
    if (budgetLines.length === 0) {
      return costTypes.filter(t => t.status === 'active');
    }
    
    if (!selectedCostCodeVuid) {
      // If no cost code selected, show all cost types that exist in budget
      const budgetCostTypeVuids = new Set(budgetLines.map(line => line.cost_type_vuid));
      return costTypes.filter(t => 
        budgetCostTypeVuids.has(t.vuid) && t.status === 'active'
      );
    }
    
    // Get cost types that exist for the selected cost code in budget lines
    const validCostTypeVuids = budgetLines
      .filter(line => line.cost_code_vuid === selectedCostCodeVuid)
      .map(line => line.cost_type_vuid);
    
    return costTypes.filter(t => 
      t.status === 'active' && validCostTypeVuids.includes(t.vuid)
    );
  };

  const validateForm = () => {
    if (!formData.project_vuid || !formData.original_budget_vuid || !formData.accounting_period_vuid || !formData.description) {
      setError('Project, Original Budget, Accounting Period, and Description are required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setError('');
      const response = await axios.post(`${baseURL}/api/internal-change-orders`, formData);
      
      setSuccessMessage('Internal Change Order created successfully!');
      
      // Store the change order data before async operations
      const newChangeOrder = response.data?.change_order;
      
      // Set selected change order and keep form open to show lines
      if (newChangeOrder) {
        setSelectedChangeOrder(newChangeOrder);
        // Don't clear formData - keep the values that were just submitted
        // The formData already has all the correct values from the form submission
        // Fetch budget lines for filtering cost codes/types
        if (newChangeOrder.original_budget_vuid || formData.original_budget_vuid) {
          fetchBudgetLines(newChangeOrder.original_budget_vuid || formData.original_budget_vuid);
        }
        // Keep showCreateForm true so the lines form appears below the header
        setChangeOrderLines([]);
      }
      
      // Refresh the data list in the background
      fetchData();
    } catch (err) {
      setError('Error creating internal change order: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleEdit = (changeOrder) => {
    setEditingChangeOrder(changeOrder);
    setFormData({
      project_vuid: changeOrder.project_vuid,
      original_budget_vuid: changeOrder.original_budget_vuid,
      accounting_period_vuid: changeOrder.accounting_period_vuid || '',
      description: changeOrder.description,
      change_order_date: changeOrder.change_order_date,
      status: changeOrder.status || 'draft',
      notes: changeOrder.notes || ''
    });
    // Fetch budget lines for filtering
    if (changeOrder.original_budget_vuid) {
      fetchBudgetLines(changeOrder.original_budget_vuid);
    }
    setShowCreateForm(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setError('');
      await axios.put(`${baseURL}/api/internal-change-orders/${editingChangeOrder.vuid}`, formData);
      
      setSuccessMessage('Internal Change Order updated successfully!');
      setShowCreateForm(false);
      setEditingChangeOrder(null);
      // Set default accounting period if only one is open
      let defaultAccountingPeriod = '';
      if (accountingPeriods.length === 1 && accountingPeriods[0].status === 'open') {
        defaultAccountingPeriod = accountingPeriods[0].vuid;
      }
      
      setFormData({
        project_vuid: '',
        original_budget_vuid: '',
        accounting_period_vuid: defaultAccountingPeriod,
        description: '',
        change_order_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      
      fetchData();
    } catch (err) {
      setError('Error updating internal change order: ' (err.response?.data?.error || err.message));
    }
  };

  const handleDelete = async (changeOrder) => {
    if (!window.confirm('Unsync this Internal Change Order in Procore (remove origin_id) and delete it from Claret? This cannot be undone.')) return;

    try {
      await axios.delete(`${baseURL}/api/internal-change-orders/${changeOrder.vuid}`);
      setSuccessMessage('Internal Change Order deleted successfully!');
      
      // Close lines form if the deleted change order is currently selected
      if (selectedChangeOrder && selectedChangeOrder.vuid === changeOrder.vuid) {
        setSelectedChangeOrder(null);
        setShowLinesForm(false);
        setChangeOrderLines([]);
      }
      
      fetchData();
    } catch (err) {
      setError('Error deleting internal change order: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleShowLines = async (changeOrder) => {
    setSelectedChangeOrder(changeOrder);
    setShowLinesForm(true);
    setShowCreateForm(false); // Close create form if open
    
    // Fetch budget lines for filtering cost codes/types
    if (changeOrder.original_budget_vuid) {
      fetchBudgetLines(changeOrder.original_budget_vuid);
    }
    
    try {
            const response = await axios.get(`${baseURL}/api/internal-change-orders/${changeOrder.vuid}/lines`);
            setChangeOrderLines(response.data);
    } catch (err) {
      console.error('Error fetching ICO lines:', err);
      setError('Error fetching change order lines: ' + err.message);
    }
  };

  const closeLinesForm = () => {
    setShowLinesForm(false);
    setSelectedChangeOrder(null);
    setChangeOrderLines([]);
    setShowCreateForm(false); // Also close create form
    setEditingChangeOrder(null);
    setNewLine({
      cost_code_vuid: '',
      cost_type_vuid: '',
      change_amount: '',
      notes: ''
    });
  };

  const handleLineInputChange = (e) => {
    const { name, value } = e.target;
    setNewLine(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLineUpdate = (lineVuid, field, value) => {
    setChangeOrderLines(prevLines => 
      prevLines.map(line => {
        if (line.vuid === lineVuid) {
          // If cost code is changing, clear the cost type
          if (field === 'cost_code_vuid') {
            return {
              ...line,
              cost_code_vuid: value,
              cost_type_vuid: '' // Clear cost type when cost code changes
            };
          }
          return {
            ...line,
            [field]: value
          };
        }
        return line;
      })
    );
  };

  const handleAddLine = () => {
    if (!newLine.cost_code_vuid || !newLine.cost_type_vuid || !newLine.change_amount) {
      setError('Cost Code, Cost Type, and Change Amount are required');
      return;
    }

    // Add line to local state (not saved to backend yet)
    const changeAmount = parseFloat(newLine.change_amount);
    if (isNaN(changeAmount)) {
      setError('Change Amount must be a valid number');
      return;
    }

    const newLineItem = {
      cost_code_vuid: newLine.cost_code_vuid,
      cost_type_vuid: newLine.cost_type_vuid,
      change_amount: changeAmount,
      notes: newLine.notes || '',
      vuid: `temp-${Date.now()}-${Math.random()}`, // Temporary ID for new lines (more unique)
      status: 'active'
    };
    
        setChangeOrderLines(prev => {
      const updated = [...prev, newLineItem];
            return updated;
    });
    setNewLine({
      cost_code_vuid: '',
      cost_type_vuid: '',
      change_amount: '',
      notes: ''
    });
    setError('');
    setSuccessMessage('Line added to grid. Click "Save All Lines" to commit changes.');
  };
  
  const handleSaveAllLines = async () => {
    if (!selectedChangeOrder) {
      setError('No change order selected');
      return;
    }
    
    try {
      setError('');
      setSuccessMessage('');
      
      // Build the list of lines to save
      // Start with existing lines in the grid
      const linesToSave = [...changeOrderLines];
      
      // Check if there's data in the new line form that needs to be added
      if (newLine.cost_code_vuid && newLine.cost_type_vuid && newLine.change_amount) {
        const changeAmount = parseFloat(newLine.change_amount);
        if (isNaN(changeAmount)) {
          setError('Change Amount must be a valid number');
          return;
        }

        // Add the form data to the list of lines to save
        const newLineItem = {
          cost_code_vuid: newLine.cost_code_vuid,
          cost_type_vuid: newLine.cost_type_vuid,
          change_amount: changeAmount,
          notes: newLine.notes || '',
          vuid: `temp-${Date.now()}-${Math.random()}`,
          status: 'active'
        };
        
        linesToSave.push(newLineItem);
      }
      
      if (linesToSave.length === 0) {
        setSuccessMessage('No lines to save.');
        return;
      }
      
            
      // Process all lines - save new ones and update existing ones
      let savedCount = 0;
      let updatedCount = 0;
      let errorCount = 0;
      
      for (const line of linesToSave) {
        try {
          const lineData = {
            cost_code_vuid: line.cost_code_vuid,
            cost_type_vuid: line.cost_type_vuid,
            change_amount: parseFloat(line.change_amount) || 0,
            notes: line.notes || '',
            status: line.status || 'active'
          };
          
          if (line.vuid.startsWith('temp-')) {
            // This is a new line, create it
                        await axios.post(
              `${baseURL}/api/internal-change-orders/${selectedChangeOrder.vuid}/lines`,
              lineData
            );
            savedCount++;
          } else {
            // This is an existing line, update it
                        await axios.put(
              `${baseURL}/api/internal-change-orders/${selectedChangeOrder.vuid}/lines/${line.vuid}`,
              lineData
            );
            updatedCount++;
          }
        } catch (lineErr) {
          console.error('Error saving individual line:', lineErr);
          errorCount++;
          setError(`Error saving line: ${lineErr.response?.data?.error || lineErr.message}. Some lines may not have been saved.`);
          // Continue saving other lines even if one fails
        }
      }
      
      // Clear the new line form after saving
      setNewLine({
        cost_code_vuid: '',
        cost_type_vuid: '',
        change_amount: '',
        notes: ''
      });
      
      // Refresh the lines from the backend to get all lines
      const response = await axios.get(`${baseURL}/api/internal-change-orders/${selectedChangeOrder.vuid}/lines`);
            setChangeOrderLines(response.data);
      
      if (errorCount === 0) {
        setSuccessMessage(`All lines saved successfully! (${savedCount} new, ${updatedCount} updated)`);
      } else {
        setError(`${errorCount} line(s) failed to save. ${savedCount} new and ${updatedCount} updated successfully.`);
      }
      
      // Refresh the change orders list to get updated total
      fetchData();
    } catch (err) {
      console.error('Error in handleSaveAllLines:', err);
      setError('Error saving lines: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteLine = (lineVuid) => {
    if (!window.confirm('Are you sure you want to delete this line?')) return;

    // If it's a temp line (not saved yet), just remove from local state
    if (lineVuid.startsWith('temp-')) {
      setChangeOrderLines(prev => prev.filter(line => line.vuid !== lineVuid));
      setSuccessMessage('Line removed from grid. Click "Save All Lines" to commit changes.');
      return;
    }

    // If it's an existing line, delete from backend
    const deleteLine = async () => {
      try {
        await axios.delete(
          `${baseURL}/api/internal-change-orders/${selectedChangeOrder.vuid}/lines/${lineVuid}`
        );
        
        setChangeOrderLines(prev => prev.filter(line => line.vuid !== lineVuid));
        setSuccessMessage('Line deleted successfully!');
        
        // Refresh the change orders to get updated total
        fetchData();
      } catch (err) {
        setError('Error deleting line: ' + (err.response?.data?.error || err.message));
      }
    };
    
    deleteLine();
  };

  const getProjectName = (projectVuid) => {
    const project = projects.find(p => p.vuid === projectVuid);
    return project ? project.project_name : 'Unknown Project';
  };

  const getBudgetDescription = (budgetVuid) => {
    const budget = budgets.find(b => b.vuid === budgetVuid);
    return budget ? budget.description : 'Unknown Budget';
  };

  const getCostCodeDisplay = (costCodeVuid) => {
    const costCode = costCodes.find(c => c.vuid === costCodeVuid);
    return costCode ? `${costCode.code} - ${costCode.description}` : 'Unknown';
  };

  const getCostTypeDisplay = (costTypeVuid) => {
    const costType = costTypes.find(t => t.vuid === costTypeVuid);
    return costType ? costType.abbreviation : 'Unknown';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '$0.00';
    const num = parseFloat(amount);
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Internal Change Orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900">Internal Change Orders</h1>
          <p className="mt-2 text-gray-600">
            Manage internal change orders that create revised budgets
          </p>
        </div>

        {/* Success Message */}
        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-green-600 font-medium">{successMessage}</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-600 font-medium">{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-6 flex justify-center">
          <button
            onClick={handleShowCreateForm}
            className="btn-claret"
            data-tooltip="Add Internal Change Order"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add Internal Change Order</span>
          </button>
        </div>

        {/* Create/Edit Form - Header */}
        {showCreateForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              {editingChangeOrder ? 'Edit Internal Change Order' : selectedChangeOrder ? `Internal Change Order: ${selectedChangeOrder.change_order_number || 'N/A'}` : 'Create Internal Change Order'}
            </h2>
            
            <form onSubmit={editingChangeOrder ? handleUpdate : handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Project Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project *
                  </label>
                  <select
                    name="project_vuid"
                    value={formData.project_vuid}
                    onChange={handleInputChange}
                    disabled={selectedChangeOrder && !editingChangeOrder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      selectedChangeOrder && !editingChangeOrder ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    required
                  >
                    <option value="">Select a Project</option>
                    {projects.map(project => (
                      <option key={project.vuid} value={project.vuid}>
                        {project.project_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Accounting Period Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accounting Period *
                  </label>
                  <select
                    name="accounting_period_vuid"
                    value={formData.accounting_period_vuid}
                    onChange={handleInputChange}
                    disabled={selectedChangeOrder && !editingChangeOrder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      selectedChangeOrder && !editingChangeOrder ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    required
                  >
                    <option value="">Select Accounting Period</option>
                    {accountingPeriods
                      .filter(period => period.status === 'open')
                      .map(period => (
                        <option key={period.vuid} value={period.vuid}>
                          {period.month}/{period.year}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Original Budget Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Original Budget *
                  </label>
                  <select
                    name="original_budget_vuid"
                    value={formData.original_budget_vuid}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      (selectedChangeOrder && !editingChangeOrder) || !formData.project_vuid ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    required
                    disabled={!formData.project_vuid || (selectedChangeOrder && !editingChangeOrder)}
                  >
                    <option value="">Select a Budget</option>
                    {budgets
                      .filter(budget => budget.project_vuid === formData.project_vuid)
                      .map(budget => (
                        <option key={budget.vuid} value={budget.vuid}>
                          {budget.description} - {formatCurrency(budget.budget_amount)}
                        </option>
                      ))}
                  </select>
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows={3}
                    disabled={selectedChangeOrder && !editingChangeOrder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      selectedChangeOrder && !editingChangeOrder ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="Describe the reason for this internal change order..."
                    required
                  />
                </div>

                {/* Change Order Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Change Order Date
                  </label>
                  <input
                    type="date"
                    name="change_order_date"
                    value={formData.change_order_date}
                    onChange={handleInputChange}
                    disabled={selectedChangeOrder && !editingChangeOrder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      selectedChangeOrder && !editingChangeOrder ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status *
                  </label>
                  <select
                    name="status"
                    value={formData.status || 'draft'}
                    onChange={handleInputChange}
                    disabled={selectedChangeOrder && !editingChangeOrder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      selectedChangeOrder && !editingChangeOrder ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    required
                  >
                    <option value="draft">Draft</option>
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    disabled={selectedChangeOrder && !editingChangeOrder}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      selectedChangeOrder && !editingChangeOrder ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                    placeholder="Additional notes..."
                  />
                </div>
              </div>

              {/* Form Buttons */}
              <div className="text-center pt-6">
                <div className="flex justify-center space-x-4">
                  <button
                    type="submit"
                    className={`btn-claret ${
                      selectedChangeOrder && !editingChangeOrder ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    disabled={selectedChangeOrder && !editingChangeOrder}
                    data-tooltip={editingChangeOrder ? 'Update Change Order' : selectedChangeOrder ? 'Change Order Created' : 'Create Change Order'}
                    >
                    <span>{editingChangeOrder ? 'Update Change Order' : selectedChangeOrder ? 'Change Order Created' : 'Create Change Order'}</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="btn-claret"
                    data-tooltip="Cancel"
                  >
                    <span>Cancel</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Internal Change Orders List */}
        {!showCreateForm && (
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Internal Change Orders
              {projectVuid && (
                <span className="text-lg font-normal text-gray-600 ml-2">
                  - {getProjectName(projectVuid)}
                </span>
              )}
            </h2>
            
            {filteredChangeOrders.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-gray-400 text-6xl mb-4">📋</div>
                <p className="text-gray-500 text-xl mb-4">No internal change orders found.</p>
                <p className="text-gray-400 mb-6">Get started by creating your first internal change order.</p>
                <button
                  onClick={handleShowCreateForm}
                  className="btn-claret"
                  data-tooltip="Add Internal Change Order"
                >
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add Internal Change Order</span>
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredChangeOrders.map((changeOrder) => (
                  <div
                    key={changeOrder.vuid}
                    className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-lg transition-shadow"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{changeOrder.change_order_number}</h3>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(changeOrder.status)}`}>
                        {changeOrder.status}
                      </span>
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-600 mb-4 line-clamp-2">{changeOrder.description}</p>
                    
                    {/* Key Info */}
                    <div className="space-y-3 mb-4">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Project:</span>
                        <span className="text-gray-900">{getProjectName(changeOrder.project_vuid)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Original Budget:</span>
                        <span className="text-gray-900">{getBudgetDescription(changeOrder.original_budget_vuid)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Date:</span>
                        <span className="text-gray-900">{new Date(changeOrder.change_order_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Total Change:</span>
                        <span className={`font-semibold ${
                          parseFloat(changeOrder.total_change_amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {formatCurrency(changeOrder.total_change_amount || 0)}
                        </span>
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleShowLines(changeOrder)}
                        className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                        data-tooltip="Manage Lines"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleEdit(changeOrder)}
                        className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                        data-tooltip="Edit change order"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => handleDelete(changeOrder)}
                        className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                        data-tooltip="Delete change order"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Change Order Lines Form - Shows when creating or when a change order is selected */}
        {(showCreateForm || (showLinesForm && selectedChangeOrder)) && (
          <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              Internal Change Order Lines
            </h2>
            
            {/* Show message if change order hasn't been created yet */}
            {showCreateForm && !selectedChangeOrder && (
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-700 font-medium text-center">
                  Please fill in the change order header fields above and click "Create Change Order" to begin adding lines.
                </p>
              </div>
            )}
            
            {/* Show header info when change order is selected */}
            {selectedChangeOrder && (
              <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedChangeOrder.change_order_number || 'N/A'}
                    </h3>
                    <p className="text-gray-600 mt-1">
                      {selectedChangeOrder.description || 'No description'}
                    </p>
                  </div>
                  {!showCreateForm && (
                    <button
                      onClick={closeLinesForm}
                      className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Add New Line Form - Only show if change order is created */}
            {selectedChangeOrder && (
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Line</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cost Code *</label>
                  <select
                    name="cost_code_vuid"
                    value={newLine.cost_code_vuid}
                    onChange={(e) => {
                      handleLineInputChange(e);
                      // Clear cost type when cost code changes
                      setNewLine(prev => ({ ...prev, cost_type_vuid: '' }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Cost Code</option>
                    {getFilteredCostCodes().map(costCode => (
                      <option key={costCode.vuid} value={costCode.vuid}>
                        {costCode.code} - {costCode.description}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Cost Type *</label>
                  <select
                    name="cost_type_vuid"
                    value={newLine.cost_type_vuid}
                    onChange={handleLineInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={!newLine.cost_code_vuid}
                  >
                    <option value="">Select Cost Type</option>
                    {getFilteredCostTypes(newLine.cost_code_vuid).map(costType => (
                      <option key={costType.vuid} value={costType.vuid}>
                        {costType.abbreviation} - {costType.cost_type}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Change Amount *</label>
                  <input
                    type="number"
                    name="change_amount"
                    value={newLine.change_amount}
                    onChange={handleLineInputChange}
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Use negative for decreases</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                  <input
                    type="text"
                    name="notes"
                    value={newLine.notes}
                    onChange={handleLineInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional notes"
                  />
                </div>
              </div>
              
              <div className="mt-4 flex gap-4">
                <button
                  type="button"
                  onClick={handleAddLine}
                  className="btn-claret"
                  data-tooltip="Add Line"
                >
                  <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>Add Line</span>
                </button>
                {changeOrderLines.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSaveAllLines}
                    className="btn-claret"
                    data-tooltip="Save All Lines"
                  >
                    <span>Save All Lines</span>
                  </button>
                )}
              </div>
            </div>
            )}

            {/* Lines Table - Only show if change order is created */}
            {selectedChangeOrder && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Change Order Lines</h3>
                {changeOrderLines.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No lines added yet. Add your first line above.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-300 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Code</th>
                        <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Type</th>
                        <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Change Amount</th>
                        <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Notes</th>
                        <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changeOrderLines.map((line, index) => (
                        <tr key={line.vuid || index} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-4 py-2 text-gray-900">
                            <select
                              value={line.cost_code_vuid || ''}
                              onChange={(e) => handleLineUpdate(line.vuid, 'cost_code_vuid', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                            >
                              <option value="">Select Cost Code</option>
                              {getFilteredCostCodes().map(costCode => (
                                <option key={costCode.vuid} value={costCode.vuid}>
                                  {costCode.code} - {costCode.description}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-gray-900">
                            <select
                              value={line.cost_type_vuid || ''}
                              onChange={(e) => handleLineUpdate(line.vuid, 'cost_type_vuid', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              disabled={!line.cost_code_vuid}
                            >
                              <option value="">Select Cost Type</option>
                              {getFilteredCostTypes(line.cost_code_vuid).map(costType => (
                                <option key={costType.vuid} value={costType.vuid}>
                                  {costType.abbreviation} - {costType.cost_type}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className={`border border-gray-300 px-4 py-2 ${
                            parseFloat(line.change_amount || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            <input
                              type="number"
                              step="0.01"
                              value={line.change_amount || ''}
                              onChange={(e) => handleLineUpdate(line.vuid, 'change_amount', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 font-medium"
                              placeholder="0.00"
                            />
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-gray-900">
                            <input
                              type="text"
                              value={line.notes || ''}
                              onChange={(e) => handleLineUpdate(line.vuid, 'notes', e.target.value)}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Optional notes"
                            />
                          </td>
                          <td className="border border-gray-300 px-4 py-2">
                            <button
                              onClick={() => handleDeleteLine(line.vuid)}
                              className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                              data-tooltip="Delete line"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InternalChangeOrders;
